import { useId, type ReactElement, type ReactNode, cloneElement, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../cn';
import { ErrorIcon } from '../icons';
import { Label } from './label';

export interface FieldProps {
  label: ReactNode;
  /** Mark optional fields; most DealDesk fields are required, so optional is the marked minority. */
  optional?: boolean;
  /** Show an asterisk (only in forms where most fields are optional). */
  required?: boolean;
  help?: ReactNode;
  error?: string | undefined;
  className?: string;
  /** The control. It receives id, aria-describedby and aria-invalid. */
  children: ReactElement<Record<string, unknown>>;
  labelHidden?: boolean;
}

/** Label above, control, help and error below. Wires accessibility attributes into the child. */
export function Field({ label, optional, required, help, error, className, children, labelHidden }: FieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : help ? helpId : undefined;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id: (children.props['id'] as string | undefined) ?? id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })
    : children;
  const controlId = (children.props['id'] as string | undefined) ?? id;
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <Label htmlFor={controlId} className={cn(labelHidden && 'sr-only')}>
        {label}
        {required ? <span className="ms-0.5 text-negative" aria-hidden="true">*</span> : null}
        {optional ? <span className="ms-1 font-normal text-ink-muted">{t('form.optional')}</span> : null}
      </Label>
      {control}
      {help && !error ? (
        <p id={helpId} className="text-meta text-ink-muted">
          {help}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="flex items-start gap-1 text-meta text-negative" role="alert">
          <ErrorIcon size={13} className="mt-px" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}
